import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { ExternalLink, AlertCircle } from "lucide-react";
import { useUpdateSiteDomain } from "../../../../hooks/useWhiteLabel";

const DOMAIN_REGEX = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;

const DomainCard = ({ site }) => {
  const { t } = useTranslation("whiteLabel");
  const toast = useToast();
  const updateDomain = useUpdateSiteDomain();

  const [isEditing, setIsEditing] = useState(false);
  const [domainInput, setDomainInput] = useState(site?.domain || "");
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    setDomainInput(site?.domain || "");
  }, [site?.domain]);

  const sslEnded = site?.ssl_process_status === "ended";
  const canRequest = sslEnded && !site?.domain;
  const canRemove = sslEnded && !!site?.domain && site?.ssl_status !== "void";

  const temporalUrl = site?.temporal_domain
    ? `https://${site.temporal_domain}`
    : null;
  const customUrl = site?.domain ? `https://${site.domain}` : null;

  const handleSave = async () => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) {
      toast.warning(t("domain.validation.required"));
      return;
    }
    if (!DOMAIN_REGEX.test(domain)) {
      toast.warning(t("domain.validation.invalid"));
      return;
    }
    try {
      await updateDomain.mutateAsync({ siteId: site.id, domain });
      handleApiSuccess(t("toasts.domainUpdated"), toast);
      setIsEditing(false);
    } catch (error) {
      handleApiError(error, toast, { defaultMessage: t("toasts.domainError") });
    }
  };

  const handleRemove = async () => {
    try {
      await updateDomain.mutateAsync({ siteId: site.id, domain: null });
      handleApiSuccess(t("toasts.domainRemoved"), toast);
      setConfirmRemove(false);
    } catch (error) {
      handleApiError(error, toast, { defaultMessage: t("toasts.domainError") });
    }
  };

  const statusMessage = () => {
    if (!site?.domain) return null;
    const status = site.ssl_status;
    const processing = site.ssl_process_status === "pending";
    if (status === "issued" && !processing) return t("domain.status.issued");
    if (status === "pre-issued") return t("domain.status.preIssued");
    if (status === "error") return t("domain.status.error");
    if (status === "pending_validation" || (status === "void" && processing))
      return t("domain.status.pendingValidation");
    if (processing && !["pending_validation", "void", "error"].includes(status))
      return t("domain.status.sslInProgress");
    if (status === "revoked") return t("domain.status.revoked");
    if (status === "cancelled") return t("domain.status.cancelled");
    if (status === "expired") return t("domain.status.expired");
    return null;
  };

  const message = statusMessage();

  return (
    <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800">
      <div className="p-4">
        <h3 className="text-lg font-medium text-white">{t("domain.title")}</h3>
      </div>

      <div className="space-y-4 border-t border-gray-700 p-4">
        {temporalUrl && (
          <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
            <div className="mb-1 text-sm text-gray-400">
              {t("domain.temporalLabel")}
            </div>
            <a
              href={temporalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-medium text-blue-400 hover:text-blue-300"
            >
              <span>{site.temporal_domain}</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        <div className="rounded-lg border border-gray-700 bg-gray-900/50 p-4">
          <div className="mb-2 text-sm text-gray-400">
            {t("domain.customLabel")}
          </div>

          {isEditing ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-400">{t("domain.cnameHint")}</p>
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                placeholder={t("domain.placeholder")}
                disabled={updateDomain.isPending}
                className="w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={updateDomain.isPending}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-600"
                >
                  {updateDomain.isPending
                    ? t("domain.saving")
                    : t("domain.save")}
                </button>
                <button
                  onClick={() => {
                    setDomainInput(site?.domain || "");
                    setIsEditing(false);
                  }}
                  disabled={updateDomain.isPending}
                  className="flex-1 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
                >
                  {t("domain.cancel")}
                </button>
              </div>
            </div>
          ) : customUrl ? (
            <a
              href={customUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 font-medium text-blue-400 hover:text-blue-300"
            >
              <span>{site.domain}</span>
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : (
            <div className="text-sm text-gray-500">{t("domain.empty")}</div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {canRequest && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="rounded-lg border border-blue-500 px-4 py-2 text-sm font-medium text-blue-400 hover:bg-blue-500/10"
              >
                {t("domain.request")}
              </button>
            )}
            {canRemove && !isEditing && !confirmRemove && (
              <button
                onClick={() => setConfirmRemove(true)}
                className="rounded-lg border border-red-500 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-500/10"
              >
                {t("domain.remove")}
              </button>
            )}
          </div>

          {confirmRemove && (
            <div className="mt-3 space-y-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
              <p className="text-sm text-red-200">
                {t("domain.removeConfirmMessage")}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleRemove}
                  disabled={updateDomain.isPending}
                  className="flex-1 rounded-lg bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:bg-gray-600"
                >
                  {updateDomain.isPending
                    ? t("domain.removing")
                    : t("domain.remove")}
                </button>
                <button
                  onClick={() => setConfirmRemove(false)}
                  disabled={updateDomain.isPending}
                  className="flex-1 rounded-lg bg-gray-600 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700"
                >
                  {t("domain.cancel")}
                </button>
              </div>
            </div>
          )}

          {message && (
            <div className="mt-3 flex items-start gap-2 text-sm text-gray-300">
              <AlertCircle className="mt-0.5 h-4 w-4 text-gray-400" />
              <span>{message}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DomainCard;
